import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, Calendar, MessageCircle } from "lucide-react";
import { getService, getServiceList } from "@/data/services";
import { useSettings } from "@/context/SettingsContext";

const renderBlocks = (blocks, depth = 0) =>
  blocks.map((b, i) => {
    if (b.type === "p") return <p key={i} className="text-foreground/75 leading-relaxed">{b.text}</p>;
    if (b.type === "b")
      return (
        <p key={i} className="text-foreground/75 leading-relaxed">
          <strong className="text-primary font-semibold">{b.label}: </strong>{b.text}
        </p>
      );
    if (b.type === "ul")
      return (
        <ul key={i} className="space-y-2 my-3">
          {b.items.map((item, k) => (
            <li key={k} className="flex items-start gap-3 text-foreground/75 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    if (b.type === "sub")
      return (
        <div key={i} className="mt-5 mb-2">
          <h4 className={`font-display ${depth === 0 ? "text-lg md:text-xl" : "text-base md:text-lg"} text-primary font-semibold mb-3`}>{b.h}</h4>
          <div className="space-y-3 pl-3 border-l-2 border-secondary">{renderBlocks(b.blocks, depth + 1)}</div>
        </div>
      );
    return null;
  });

const ServicoDetail = () => {
  const { slug } = useParams();
  const { settings } = useSettings();
  const service = getService(slug);
  const list = getServiceList();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!service) return <Navigate to="/servicos" replace />;

  const idx = list.findIndex((s) => s.slug === slug);
  const prev = idx > 0 ? list[idx - 1] : null;
  const next = idx < list.length - 1 ? list[idx + 1] : null;
  const wa = settings?.contact?.whatsapp || "5565996516708";

  return (
    <div data-testid={`servico-${slug}`}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary via-primary to-primary/85 text-primary-foreground pt-28 md:pt-32 pb-12 md:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-30" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-accent blob" />
        <div className="container-pad relative">
          <Link to="/servicos" className="inline-flex items-center gap-1.5 text-xs text-primary-foreground/70 hover:text-accent mb-5 transition" data-testid="back-to-servicos">
            <ChevronLeft className="w-4 h-4" /> Voltar para Serviços
          </Link>
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="w-8 h-px bg-accent" />
                <span className="text-[11px] tracking-[0.3em] text-accent font-semibold uppercase">{String(idx + 1).padStart(2, "0")} / {String(list.length).padStart(2, "0")} · OTORRINO</span>
              </div>
              <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-light leading-tight mb-4 text-balance">{service.title}</h1>
              <p className="text-primary-foreground/80 text-sm md:text-base max-w-2xl leading-relaxed">{service.intro}</p>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="md:col-span-2">
              <div className="aspect-square rounded-3xl overflow-hidden bg-white/5 backdrop-blur p-6 border border-white/10">
                <img src={service.image} alt={service.title} className="w-full h-full object-contain" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container-pad py-12 md:py-16 grid lg:grid-cols-4 gap-10">
        <article className="lg:col-span-3 space-y-10">
          {service.sections.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="border-l-4 border-accent pl-5"
              data-testid={`section-${i}`}
            >
              <h2 className="font-display text-xl md:text-2xl lg:text-3xl text-primary font-semibold mb-4">{s.h}</h2>
              <div className="space-y-3">{renderBlocks(s.blocks)}</div>
            </motion.div>
          ))}

          {/* CTA inline */}
          <div className="bg-gradient-to-br from-accent via-accent to-emerald-600 text-accent-foreground rounded-3xl p-8 md:p-10 relative overflow-hidden mt-12">
            <div className="absolute inset-0 pattern-dots opacity-20" />
            <div className="relative">
              <h3 className="font-display text-xl md:text-3xl mb-2 font-light">Precisa de avaliação?</h3>
              <p className="text-sm md:text-base opacity-90 mb-5">Agende sua consulta e tire suas dúvidas com o Dr. Alonso Neto.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contato" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-primary font-medium text-sm hover:-translate-y-0.5 transition">
                  <Calendar className="w-4 h-4" /> Agendar Consulta
                </Link>
                <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-white text-white font-medium text-sm hover:bg-white hover:text-primary transition">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Prev / Next */}
          <div className="grid sm:grid-cols-2 gap-3 mt-8">
            {prev ? (
              <Link to={`/servicos/${prev.slug}`} className="group p-5 bg-white border border-border rounded-2xl hover:border-accent transition" data-testid="prev-service">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">← Anterior</div>
                <div className="font-display text-base md:text-lg text-primary font-semibold group-hover:text-accent transition">{prev.title}</div>
              </Link>
            ) : <div />}
            {next && (
              <Link to={`/servicos/${next.slug}`} className="group p-5 bg-white border border-border rounded-2xl hover:border-accent transition text-right" data-testid="next-service">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Próximo →</div>
                <div className="font-display text-base md:text-lg text-primary font-semibold group-hover:text-accent transition">{next.title}</div>
              </Link>
            )}
          </div>
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <div className="sticky top-28 bg-white border border-border rounded-3xl p-5 ring-medical">
            <div className="text-[11px] uppercase tracking-widest text-accent font-semibold mb-4">Todos os Serviços</div>
            <nav className="space-y-1">
              {list.map((s) => (
                <Link
                  key={s.slug}
                  to={`/servicos/${s.slug}`}
                  className={`block px-3 py-2 rounded-lg text-sm transition ${s.slug === slug ? "bg-accent text-accent-foreground font-medium" : "text-foreground/75 hover:bg-secondary/40 hover:text-accent"}`}
                  data-testid={`sidebar-link-${s.slug}`}
                >
                  {s.title}
                </Link>
              ))}
            </nav>
            <Link to="/contato" className="mt-5 inline-flex w-full items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition">
              Agendar consulta <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default ServicoDetail;
